// Pending-run stash — the wizard's run inputs held across the Stripe Checkout
// round-trip.
//
// Paying for an analysis means a full-page redirect to Stripe's hosted checkout
// and back to `/?paid=1`. That reload reconstructs the SPA from scratch, so the
// in-memory dropped file (a `File`, which can't live in localStorage) is gone.
// This stashes just enough to re-launch the run on return — the file's bytes,
// its name/type/meta/adapter, and the code/tier — so the user doesn't have to
// re-drop after paying.
//
// STORE BYTES, NOT A Blob/File. A `File` made from `new File([string])` is
// backed by the originating document's blob store; persisting the File object
// in IndexedDB is fragile across a navigation away and back — Safari (and some
// Chromium builds) hand back a ZERO-LENGTH blob, which surfaced as
// "corpus_csv is empty" after real payments (2026-06-20). An ArrayBuffer is
// structured-cloned BY VALUE into IndexedDB, so the bytes are durable on every
// browser. We reconstruct the File on restore.
//
// SHORT-LIVED BY DESIGN. The trade (case content briefly on disk in IndexedDB)
// was an explicit product call (Rich, 2026-06-19: real measurement before
// paying, exact price, no re-drop). We bound the exposure:
//   - written only at Authorize, immediately before the redirect;
//   - deleted the moment the run launches (the file then lives only on the
//     server, for the run, under the existing auto-purge); and
//   - ignored + cleared if older than PENDING_RUN_TTL_MS, plus a plain-boot
//     janitor (App.tsx) that purges any abandoned stash on the next app open.
//
// IndexedDB has no jsdom shim, so — like _mapping_profiles.ts — the backing
// store is pluggable: real IndexedDB in the browser, an injected fake in tests.

export interface PendingRun {
  file: File;
  metaJson: string;
  adapter: string;
  accessCode: string;
  tierName: string;
  // The confirmed notify-email OVERRIDE in effect at Authorize time (App's
  // `notifyEmail` state) — null when no override was set (either the code's
  // server-linked address was used as-is, or there simply is none yet).
  // Restored on Checkout return (launch-audit fix, 2026-07-04): without this,
  // a self-serve code with no server-linked email lost the just-typed
  // address across the full-page Stripe round-trip — a post-payment preview
  // cancel + "run again" retry then showed the EMPTY email ask again, even
  // though the user had already confirmed one this same visit.
  notifyEmail: string | null;
  savedAt: number; // epoch ms — freshness guard
}

export type PendingRunInput = Omit<PendingRun, "savedAt">;

// What actually lands in IndexedDB: the file's BYTES (durable), not the File.
export interface StoredRecord {
  data: ArrayBuffer;
  fileName: string;
  fileType: string;
  metaJson: string;
  adapter: string;
  accessCode: string;
  tierName: string;
  notifyEmail: string | null;
  savedAt: number;
}

// Two hours, not the original 30 minutes: the first public walk-in
// (2026-07-09) paid via Apple Pay, sat taking notes, and returned to an
// expired stash — a stressed user pausing 30+ minutes mid-checkout is
// NORMAL for this population, and expiry costs them a confusing re-upload.
// Two hours still bounds the shared-device window; anything older is an
// abandoned attempt; never auto-launch it (and the on-read clear bounds how
// long an abandoned stash can sit if the user only ever returns via ?paid).
// The plain-boot janitor in App.tsx is the primary cleanup; this is a backstop.
export const PENDING_RUN_TTL_MS = 2 * 60 * 60 * 1000;

export interface PendingRunStore {
  get(): Promise<StoredRecord | null>;
  set(value: StoredRecord): Promise<void>;
  clear(): Promise<void>;
}

const DB_NAME = "aurora.cloud.pendingRun";
const STORE_NAME = "kv";
const RECORD_KEY = "current";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE_NAME);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function indexedDbStore(): PendingRunStore {
  return {
    async get() {
      const db = await openDb();
      try {
        return await new Promise<StoredRecord | null>((resolve, reject) => {
          const tx = db.transaction(STORE_NAME, "readonly");
          const req = tx.objectStore(STORE_NAME).get(RECORD_KEY);
          req.onsuccess = () =>
            resolve((req.result as StoredRecord | undefined) ?? null);
          req.onerror = () => reject(req.error);
        });
      } finally {
        db.close();
      }
    },
    async set(value) {
      const db = await openDb();
      try {
        // Await the TRANSACTION's completion (not just the request's success):
        // the commit is what makes the write durable before we navigate to
        // Stripe. Resolving on request.onsuccess could let the redirect abort
        // an uncommitted transaction.
        await new Promise<void>((resolve, reject) => {
          const tx = db.transaction(STORE_NAME, "readwrite");
          tx.objectStore(STORE_NAME).put(value, RECORD_KEY);
          tx.oncomplete = () => resolve();
          tx.onerror = () => reject(tx.error);
          tx.onabort = () => reject(tx.error);
        });
      } finally {
        db.close();
      }
    },
    async clear() {
      const db = await openDb();
      try {
        await new Promise<void>((resolve, reject) => {
          const tx = db.transaction(STORE_NAME, "readwrite");
          tx.objectStore(STORE_NAME).delete(RECORD_KEY);
          tx.oncomplete = () => resolve();
          tx.onerror = () => reject(tx.error);
          tx.onabort = () => reject(tx.error);
        });
      } finally {
        db.close();
      }
    },
  };
}

function defaultStore(): PendingRunStore | null {
  try {
    if (typeof indexedDB !== "undefined") return indexedDbStore();
  } catch {
    /* indexedDB may be absent (jsdom) or blocked (locked-down browser) */
  }
  return null;
}

/** Stash run inputs before the Checkout redirect. Reads the file's bytes up
 *  front (durable storage). Best-effort: a failed stash just degrades to a
 *  re-drop on return, never a thrown error on the pay path. */
export async function savePendingRun(
  input: PendingRunInput,
  store: PendingRunStore | null = defaultStore(),
  now: number = Date.now(),
): Promise<void> {
  if (!store) return;
  try {
    const data = await input.file.arrayBuffer();
    await store.set({
      data,
      fileName: input.file.name,
      fileType: input.file.type,
      metaJson: input.metaJson,
      adapter: input.adapter,
      accessCode: input.accessCode,
      tierName: input.tierName,
      notifyEmail: input.notifyEmail,
      savedAt: now,
    });
  } catch {
    /* swallow — the redirect still proceeds; worst case the user re-drops */
  }
}

/** Load a FRESH stash and rebuild the File from its bytes, or null. A stale,
 *  unreadable, or empty record is cleared and treated as absent so it can't
 *  silently auto-run an empty corpus. */
export async function loadPendingRun(
  opts: { now?: number; store?: PendingRunStore | null } = {},
): Promise<PendingRun | null> {
  const store = opts.store === undefined ? defaultStore() : opts.store;
  if (!store) return null;
  const now = opts.now ?? Date.now();
  let rec: StoredRecord | null;
  try {
    rec = await store.get();
  } catch {
    return null;
  }
  const stale =
    !rec ||
    typeof rec.savedAt !== "number" ||
    now - rec.savedAt > PENDING_RUN_TTL_MS;
  const empty = !rec || !rec.data || rec.data.byteLength === 0;
  if (!rec || stale || empty) {
    if (rec) {
      try {
        await store.clear();
      } catch {
        /* ignore */
      }
    }
    return null;
  }
  return {
    file: new File([rec.data], rec.fileName, { type: rec.fileType }),
    metaJson: rec.metaJson,
    adapter: rec.adapter,
    accessCode: rec.accessCode,
    tierName: rec.tierName,
    // ?? null: a stash written before this field existed reads as "no
    // override" rather than undefined — degrades to the pre-fix behavior
    // (empty ask / re-fetched linkedEmail), never a crash.
    notifyEmail: rec.notifyEmail ?? null,
    savedAt: rec.savedAt,
  };
}

export async function clearPendingRun(
  store: PendingRunStore | null = defaultStore(),
): Promise<void> {
  if (!store) return;
  try {
    await store.clear();
  } catch {
    /* ignore */
  }
}

// Plain-boot janitor grace (seam audit 2026-07-22): a stash younger than
// this SURVIVES a plain boot — a second same-origin tab opened while the
// first is mid-round-trip at Stripe boots plain, and an unconditional clear
// destroyed the in-flight stash. Anything older clears at boot as before;
// loadPendingRun's own TTL (above) bounds the absolute exposure window.
export const PENDING_RUN_JANITOR_MIN_AGE_MS = 30 * 60 * 1000;
